import React, { useEffect, useState } from "react";
import { Typography, Spin, Empty, Button, Space } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import jobDescriptionService from "../../Service/job.description.service";
import "./JobPostsStatusGraph.scss";
import { useNavigate } from "react-router-dom";
import { CHART_COLORS } from "../JobPostDetails/components/StatsAndAnalytics/utils/chartColors";

const { Title, Text } = Typography;

interface JobPostStatus {
  _id: string;
  title: string;
  applied: number;
  notQualified: number;
  total: number;
}

interface JobPostsStatusGraphProps {
  onHasGraphsChange?: (hasGraphs: boolean) => void;
}

const JobPostsStatusGraph: React.FC<JobPostsStatusGraphProps> = ({ onHasGraphsChange }) => {
  const [jobPostStatuses, setJobPostStatuses] = useState<JobPostStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  useEffect(() => {
    const fetchJobPosts = async () => {
      try {
        setLoading(true);
        const response = await jobDescriptionService.getAllJobDescriptions(
          1,
          10
        ); 

        if (response && response.data && response.data.length > 0) {
          const statusData = response.data
            .filter((job: any) => {
              return job.isActive === true; 
            })
            .map((job: any) => {
              const applied = job.numberOfApplications || 0;
              const qualified = job.numberOfQualifiedApplications || 0;
              const pending = job.numberOfPendingInterviews || 0;
              const hired = job.numberOfHiredApplications || 0;

              // Calculate not qualified as total applications minus qualified ones
              const notQualified = applied - qualified;

              return {
                _id: job._id,
                title: job.title,
                applied: qualified, // Show qualified as "applied"
                notQualified: notQualified > 0 ? notQualified : 0, // Show non-qualified
                total: applied,
                createdAt: job.createdAt || job.updatedAt || new Date(0), // Use createdAt or updatedAt for sorting
              };
            })
            .sort((a: JobPostStatus & { createdAt: Date | string }, b: JobPostStatus & { createdAt: Date | string }) => {
              // First sort by total applications (descending), then by creation date (most recent first)
              if (b.total !== a.total) {
                return b.total - a.total;
              }
              // If applications are equal, prefer more recently posted
              const dateA = new Date(a.createdAt).getTime();
              const dateB = new Date(b.createdAt).getTime();
              return dateB - dateA;
            })
            .slice(0, 3) // Limit to top 3
            .map(({ createdAt, ...job }: JobPostStatus & { createdAt: Date | string }) => job); // Remove createdAt from final data
          setJobPostStatuses(statusData);
        } else {
          console.log("JobPostsStatusGraph: No response data or empty response");
        }
      } catch (err) {
        console.error("JobPostsStatusGraph: Failed to fetch job posts:", err);
        setError("Failed to load job post data");
      } finally {
        setLoading(false);
      }
    };

    fetchJobPosts();
  }, []);

  // Check if any jobs have applications
  const hasApplications = jobPostStatuses.some((job) => job.total > 0);
  const hasGraphs = hasApplications && jobPostStatuses.length > 0;

  // Notify parent when graphs are available (must be called before any early returns)
  useEffect(() => {
    if (onHasGraphsChange) {
      if (loading || error) {
        onHasGraphsChange(false);
      } else if (jobPostStatuses.length === 0) {
        onHasGraphsChange(false);
      } else {
        onHasGraphsChange(hasGraphs);
      }
    }
  }, [loading, error, jobPostStatuses.length, hasGraphs, onHasGraphsChange]);

  if (loading) {
    return (
      <div className="job-posts-status-graph__loading-container">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-posts-status-graph__error-container">
        <Text type="danger">{error}</Text>
      </div>
    );
  }

  // Check if we have any active job posts
  if (jobPostStatuses.length === 0) {
    return (
      <div className="job-posts-status-graph__empty-container">
        <Empty
          description="No active job posts found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
        <div className="job-posts-status-graph__create-button-container">
          <Button
            onClick={() => {
              navigate("/add-job-post");
            }}
            type="primary"
            shape="round"
            icon={<PlusOutlined />}
            size="large"
            className="job-posts-status-graph__create-button"
            style={{ cursor: "pointer" }}
          >
            Click here to create job post
          </Button>
        </div>
      </div>
    );
  }

  if (!hasApplications) {
    return (
      <div className="job-posts-status-graph__fallback-container">
        <Title level={4} className="job-posts-status-graph__title">
          Active Job Posts
        </Title>

        <div className="job-posts-status-graph__fallback-content">
          <Text
            type="secondary"
            className="job-posts-status-graph__fallback-title"
          >
            {jobPostStatuses.length} Active Job Posts
          </Text>
          <Text
            type="secondary"
            className="job-posts-status-graph__fallback-subtitle"
          >
            No applications received yet
          </Text>
        </div>

        <div className="job-posts-status-graph__footer">
          <Text
            type="secondary"
            className="job-posts-status-graph__footer-text"
          >
            Showing {jobPostStatuses.length} active job posts
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="job-posts-status-graph">
      <div className="job-posts-status-graph__chart-container">
        <div className="job-posts-status-graph__progress-list">
          {jobPostStatuses.map((job) => {
            const total = job.total || 1; // Avoid division by zero
            const qualifiedPercent = ((job.applied || 0) / total) * 100;
            const unqualifiedPercent = ((job.notQualified || 0) / total) * 100;
            
            const handleItemClick = (e: React.MouseEvent) => {
              e.stopPropagation();
              navigate(`/job-posts/${job._id}?tabIndex=applications`);
            };

            const handleKeyDown = (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/job-posts/${job._id}?tabIndex=applications`);
              }
            };

            return (
              <div 
                key={job._id} 
                className="job-posts-status-graph__progress-item"
                onClick={handleItemClick}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={0}
                aria-label={`View details for ${job.title}`}
              >
                <div className="job-posts-status-graph__progress-header">
                  <Text strong className="job-posts-status-graph__job-title">
                    {job.title}
                  </Text>
                  <Text type="secondary" className="job-posts-status-graph__job-total">
                    {job.total} Total
                  </Text>
                </div>
                <div className="job-posts-status-graph__progress-bar">
                  <div className="job-posts-status-graph__progress-track">
                    <div
                      className="job-posts-status-graph__progress-segment job-posts-status-graph__progress-segment--qualified"
                      style={{
                        width: `${qualifiedPercent}%`,
                        backgroundColor: CHART_COLORS.success,
                      }}
                    />
                    <div
                      className="job-posts-status-graph__progress-segment job-posts-status-graph__progress-segment--unqualified"
                      style={{
                        width: `${unqualifiedPercent}%`,
                        backgroundColor: CHART_COLORS.placeholder,
                      }}
                    />
                  </div>
                </div>
                <div className="job-posts-status-graph__progress-details">
                  <Space split="|" size="small">
                    <Text type="secondary" className="job-posts-status-graph__progress-text">
                      <span style={{ color: CHART_COLORS.success, fontWeight: 500 }}>
                        {job.applied || 0}
                      </span>{" "}
                      Qualified ({qualifiedPercent.toFixed(1)}%)
                    </Text>
                    <Text type="secondary" className="job-posts-status-graph__progress-text">
                      <span style={{ color: CHART_COLORS.placeholder, fontWeight: 500 }}>
                        {job.notQualified || 0}
                      </span>{" "}
                      Unqualified ({unqualifiedPercent.toFixed(1)}%)
                    </Text>
                  </Space>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default JobPostsStatusGraph;
